// ============================================================================
// vpn/VpnConfigBlock.tsx — клиентский конфиг профиля: «Получить конфиг» →
// серверный WireGuard INI / .ovpn / .mobileconfig / URI-ссылка + QR (рисует
// СЕРВЕР, PNG base64) + скачивание файла нужного формата. SSoT — сервер.
// ============================================================================

import { useState } from 'react';
import { QrCode, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { createLogger } from '@/lib/logger';
import { copyToClipboard } from '@/lib/clipboard';
import { isNotFoundError } from '@/lib/errors';
import { VPN_LABEL_CLS } from './uiClasses';
import { VPN_CONFIG_FORMAT_LABELS, vpnConfigExt } from './vpnConfigFormat';

const log = createLogger('VpnConfigBlock');

interface VpnConfig {
  protocol: string;
  config: string;
  configFormat: string;
  qrPngBase64: string;
  shareLink: string;
}

interface VpnConfigBlockProps {
  profileCode: string;
  busy: boolean;
  protocolLabel: (code: string) => string;
}

export function VpnConfigBlock({ profileCode, busy, protocolLabel }: VpnConfigBlockProps) {
  const [config, setConfig] = useState<VpnConfig | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchConfig = async () => {
    if (!profileCode) {
      useToastStore.getState().showError('Сначала сохраните профиль');
      return;
    }
    setLoading(true);
    try {
      const r = await getSDK().vpn.getVpnConfig({ profileCode });
      setConfig({
        protocol: r.protocol, config: r.config, configFormat: r.configFormat,
        qrPngBase64: r.qrPngBase64, shareLink: r.shareLink,
      });
    } catch (e) {
      log.error('get vpn config', { error: e, code: profileCode });
      if (isNotFoundError(e)) useToastStore.getState().showError('Профиль не найден');
      else useToastStore.getState().showError(e instanceof Error ? e.message : 'Не удалось получить конфиг');
    } finally {
      setLoading(false);
    }
  };

  const downloadConfig = () => {
    if (!config) return;
    const ext = vpnConfigExt(config.configFormat);
    const blob = new Blob([config.config], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${profileCode || 'vpn'}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border-t pt-3 space-y-2">
      <Button variant="outline" size="sm" onClick={() => void fetchConfig()} disabled={busy || loading || !profileCode}>
        <QrCode className="h-3.5 w-3.5 mr-1" /> {loading ? 'Получение…' : 'Получить конфиг'}
      </Button>
      {config && (
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
          {config.qrPngBase64 && (
            <img alt="QR конфига" className="rounded border bg-white p-1 w-[200px] h-[200px]"
              src={`data:image/png;base64,${config.qrPngBase64}`} />
          )}
          <div className="space-y-2 min-w-0">
            <div className={VPN_LABEL_CLS}>
              {protocolLabel(config.protocol)} · формат: {VPN_CONFIG_FORMAT_LABELS[config.configFormat] || config.configFormat}
            </div>
            <pre className="text-[11px] leading-tight bg-muted/40 border rounded p-2 overflow-auto max-h-[220px] whitespace-pre-wrap">{config.config}</pre>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadConfig}>
                Скачать .{vpnConfigExt(config.configFormat)}
              </Button>
              <Button variant="outline" size="sm" onClick={() => void copyToClipboard(config.shareLink || config.config)}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Копировать
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
